import { produce } from 'immer';

import { VariableScope } from 'components/automations/components/nodeTypes/types';

import { AutomationNodeTypes } from '../types/automations.types';
import {
  CreateAutomationIntegrationNodeTestApiResponse,
  type ReadAutomationApiResponse,
} from '../types/automationsApi.types';

import {
  CONTACT_CUSTOM_ATTRIBUTE_PREFIX,
  CONTACT_CUSTOM_ATTRIBUTE_VARIABLETYPE,
  CONVERSATION_CUSTOM_ATTRIBUTE_PREFIX,
  CONVERSATION_CUSTOM_ATTRIBUTE_VARIABLETYPE,
} from './constants';

/**
 * Look up all condition nodes and do the following:
 *
 * 1. Iterate over all rules
 * 2. If the variable type of the rule is either a contact or conversation custom
 *    attribute, henceforth called custom attribute, then:
 * 3. Transform the custom attribute from
 *
 * { scope: 'system', variableType:
 * 'contact_custom_attribute:5772494a-302c-41c3-a4c4-fda05fd33ea2' }
 *
 * To
 *
 * { scope: 'system' variableType: 'conversationCustomAttribute', id:
 * '5772494a-302c-41c3-a4c4-fda05fd33ea2', }
 */
export const transformReadAutomationResponse = (
  response: ReadAutomationApiResponse,
): ReadAutomationApiResponse => {
  return produce(response, (draft) => {
    draft.data.actions.forEach((action) => {
      if (
        action.nodeType === AutomationNodeTypes.CONDITION ||
        action.nodeType === AutomationNodeTypes.INIT
      ) {
        if (!action.payload) {
          // payload can be null;
          return;
        }

        // Rules can be empty
        (action?.payload?.rules ?? []).forEach((rule) => {
          if (
            rule.variable?.variableType?.startsWith(
              CONTACT_CUSTOM_ATTRIBUTE_PREFIX,
            )
          ) {
            rule.variable.id = rule.variable.variableType.split(':')[1];
            rule.variable.variableType = CONTACT_CUSTOM_ATTRIBUTE_VARIABLETYPE;

            /**
             * Unwrap the array of objects to an array of strings in order to be
             * able to use the custom attribute value in useFieldArray.
             */
            if (rule.match.scope === VariableScope.INPUT) {
              const apiScopeValues = rule.match.values;
              rule.match.values = [apiScopeValues.map(({ value }) => value)];
            }
          } else if (
            rule.variable?.variableType?.startsWith(
              CONVERSATION_CUSTOM_ATTRIBUTE_PREFIX,
            )
          ) {
            rule.variable.id = rule.variable.variableType.split(':')[1];
            rule.variable.variableType =
              CONVERSATION_CUSTOM_ATTRIBUTE_VARIABLETYPE;

            if (rule.match.scope === VariableScope.INPUT) {
              const apiScopeValues = rule.match.values;
              rule.match.values = [apiScopeValues.map(({ value }) => value)];
            }
          }
        });
      }
    });
  });
};

export const transformCreateAutomationIntegrationNodeTestResponse = (
  response: CreateAutomationIntegrationNodeTestApiResponse,
): CreateAutomationIntegrationNodeTestApiResponse => {
  return produce(response, (draft) => {
    try {
      const obj = JSON.parse(draft.data.response.body);
      draft.data.response.body = obj;
    } catch {
      // Use the string representation of the body if it can't be parsed as JSON.
    }
  });
};
